<script lang="ts">
    import { onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import { get } from "svelte/store";
    import TopBar from "../Components/TopBar.svelte";
    import type { Race } from "../Model/Race";
    import type { Route } from "../Model/Route";
    import { geolocation, picturesService, raceService, routesService, user } from "../Services";
    import { Facebook, WhatsApp, Twitter, Telegram } from 'svelte-share-buttons-component';
    import type { Picture } from "../global";
    import { makeTimeString } from "../Services/Helpers";

    const canvasWidth = 404;
    const canvasHeight = 600;
    const userImageX = 298;
    const userImageY = 493;
    const userImageSize = 73;

    let scoreCanvas: HTMLCanvasElement;
    let successSpan: HTMLSpanElement;
    let preparingImage = false;
    let picture: Picture | null = null;

    onMount(getRoute);

    async function getRoute() {

        if (!(await raceService.hasRace()) || !$user) {
            navigate('/menu');
            return;
        }
        
        const race = await raceService.loadRace();

        if (!get(race.isEnded)) {
            navigate('/menu');
            return;
        }

        const route = await routesService.getRoute(race.routeId);

        drawScore(race, route);
    }

    async function drawScore(race: Race, route: Route) {
        if (!scoreCanvas) {
            throw new Error('No se puede dibujar la puntuación mientras el canvas no esté listo.');
        }
       
        const successColor = window.getComputedStyle(successSpan).color;
        const ctx = scoreCanvas.getContext('2d');
        const logo = await loadImage('/images/score-board-template.svg');
        const response = await fetch($user.image);
        const userImageBlob = await response.blob();
        const userImageUrl = URL.createObjectURL(userImageBlob);
        const userImage = await loadImage(userImageUrl);

        ctx.fillStyle = route.color;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        
        ctx.drawImage(logo, 0, 0);

        ctx.font = "bold 35px Roboto";
        ctx.fillStyle = successColor;
        ctx.textAlign = 'center';
        
        writeText(ctx, canvasWidth / 2, 180, 40, `
            ¡¡Lo lograste
            ${$user.name}!!
        `);

        ctx.font = "20px Roboto";
        ctx.fillStyle = '#fff';

        writeText(ctx, canvasWidth / 2, 307, 25, `"${route.name}"`);

        ctx.font = "23px Roboto";
        ctx.fillStyle = successColor;

        const distance = route.meta.distance.toFixed(1).replace('.', ',');
        const raceTime = makeTimeString(race.getRaceTime());

        writeText(ctx, canvasWidth / 2, 350, 30, `
            ¡Has recorrido 
            ${distance} km en ${raceTime}
            obteniendo ${get(race.points)} puntos!
        `);

        ctx.beginPath();
        ctx.arc(
            userImageX + userImageSize / 2,
            userImageY + userImageSize / 2,
            userImageSize / 2,
            0,
            2 * Math.PI
        );

        ctx.clip();
        ctx.drawImage(userImage, userImageX, userImageY, userImageSize, userImageSize);

        URL.revokeObjectURL(userImageUrl);

        scoreCanvas.toBlob((blob: Blob) => uploadScoreImage(blob, race, route), 'image/jpeg');
    }

    function loadImage(src: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image: HTMLImageElement = document.createElement('img');
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', error => reject(error));
            image.src = src;
        });
    }

    function writeText(ctx: CanvasRenderingContext2D, x: number, y: number, lineHeight: number, text: string) {
        text.split('\n').map(line => line.trim()).filter(line => !!line).forEach((line, lineNumber) => {
            ctx.fillText(line, x, y + (lineHeight * lineNumber));
        });
    }

    async function uploadScoreImage(blob: Blob, race: Race, route: Route) {
        if (preparingImage) {
            return;
        }
        
        try {

            preparingImage = true;

            const { lon, lat } = await geolocation.getPosition();

            const labels = [
                'rutas-saludables-pto-cruz',
                'score-image',
                race.routeId,
            ];

            const pictureId = await picturesService.uploadPicture(blob, labels, {
                coords: `${lon},${lat}`,
                user_name: $user.name,
                user_image: $user.image,
            });

            picture = await picturesService.findById(pictureId);

        } catch (error) {
              console.error(error);
        } finally {
            preparingImage = false;
        }
    }

</script>
<style lang="scss">

    .share-score {
        display: flex;
        flex-direction: column;
        background-color: var(--color-secondary);
    }

    .share-score-content {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .score-canvas {
        width: calc(100vmin - 2rem);
    }

    .share-bar {
        height: 60px;
        margin-top: 0.5rem;
    }

</style>

<div class="share-score view-container">
    <TopBar title="Comparte tu resultado" on:back={() => navigate('/menu')} />

    <div class="share-score-content">
        <span style="color: var(--color-success);" bind:this={successSpan}></span>
        <canvas class="score-canvas" width={canvasWidth} height={canvasHeight} bind:this={scoreCanvas}></canvas>

        <div class="share-bar">
            {#if picture}
            <Facebook url={picture.url.medium} />
            <WhatsApp url={picture.url.medium} />
            <Twitter url={picture.url.medium} />
            <Telegram url={picture.url.medium} />
            {/if}
        </div>
    </div>

</div>
